import {gql} from '@apollo/client';

import {assert, assertAndContinue} from 'Asserts';
import {SchemaTag} from 'folder-items/FileConstants';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {BoxCreationTableRowMetadata} from 'notebook/integrations/samples/BoxCreationStructuredTableAdapter';
import {ColumnGroup} from 'notebook/structured-tables/columns/ColumnDefinitions';
import {LiveCellValue} from 'notebook/structured-tables/LiveCellValues';
import {
  bulkFetchParentStorableCapacityModel,
  isParentStorableCapacityInfo,
  isUnfinalizedParentStorableSample,
  ParentStorableCapacityInfo,
  readParentStorableCapacityFromCache,
} from 'notebook/structured-tables/row-updaters/inventoryParentStorableRowUpdaterUtils';
import {buildStandardUpdatedCells} from 'notebook/structured-tables/row-updaters/updateRowUtils';
import {
  SampleLookupInfo,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
} from 'notebook/structured-tables/StructuredTableConstants';
import isColumnForSchemaField from 'notebook/structured-tables/util/isColumnForSchemaField';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {
  convertTagToTypedValue,
  getTypedValueFromStorableSummaries,
} from 'notebook/tables/typed-values/DataTypes';
import APIUtil from 'util/APIUtil';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  BulkFetchBoxInfoForRowQuery,
  BulkFetchBoxInfoForRowQueryVariables,
} from './__generated/BulkFetchBoxInfoForRowQuery';

type BoxInfo = ExtractByTypename<BulkFetchBoxInfoForRowQuery['storables'][number], 'BoxNode'>;

export const BULK_BOX_INFO_FOR_ROW_UPDATE_QUERY = gql`
  query BulkFetchBoxInfoForRowQuery($boxIds: [StrictString!]!) @skipAudit @engComponent(name: INVENTORY) {
    storables(ids: $boxIds) {
      ...BoxLiveDataGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('BoxLiveData').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

async function _bulkFetchModel(
  sampleLookupInfos: Array<SampleLookupInfo>
): Promise<Array<BoxInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  const parentStorablesPromise = bulkFetchParentStorableCapacityModel(
    sampleLookupInfos
      .filter((info) => isUnfinalizedParentStorableSample(info))
      .map((info) => info.idForLookup)
  );
  const boxesPromise = doQuerySafe<BulkFetchBoxInfoForRowQuery, BulkFetchBoxInfoForRowQueryVariables>(
    {query: BULK_BOX_INFO_FOR_ROW_UPDATE_QUERY},
    {
      variables: {
        boxIds: sampleLookupInfos
          .filter((info) => !isUnfinalizedParentStorableSample(info))
          .map((info) => info.idForLookup),
      },
      fetchPolicy: 'network-only',
    }
  );
  const [parentStorables, {storables}] = await Promise.all([parentStorablesPromise, boxesPromise]);
  // We know none of these will be a ContainerNode
  return [...(storables as Array<BoxInfo | InaccessibleInfoGraphQL>), ...parentStorables];
}

const BoxCreationTableRowUpdater: StructuredTableRowUpdater<
  BoxInfo | ParentStorableCapacityInfo,
  BoxCreationTableRowMetadata
> = {
  fetchModel(
    sampleLookupInfo: SampleLookupInfo
  ): Promise<BoxInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
    return _bulkFetchModel([sampleLookupInfo]).then((results) => results[0]);
  },

  bulkFetchModel(
    sampleLookupInfos: Array<SampleLookupInfo>
  ): Promise<Array<BoxInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
    return _bulkFetchModel(sampleLookupInfos);
  },

  updateCell: (
    model: BoxInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columnGroup: ColumnGroup | null,
    column: StructuredTableColumn
  ): LiveCellValue => {
    // No updates to cells for capacity info, which is used only in unfinalized rows & doesn't affect the
    // information stored in the table
    if (isParentStorableCapacityInfo(model)) {
      return 'NO_VALUE';
    }

    const box = model;
    if (isInaccessible(box)) {
      return 'NO_VALUE';
    }

    const location = box.parentLocation;
    assert(location != null, "Box's location should be specified in box creation table.");

    if (column.columnType === TableColumnType.BOX) {
      if (isInaccessible(box)) {
        return 'NO_VALUE';
      }
      assert(box.__typename === 'BoxNode', 'Retrieved storable must be a box.');

      return getTypedValueFromStorableSummaries([box]);
    }

    if (column.columnType === TableColumnType.PARENT_STORABLE) {
      if (isInaccessible(location)) {
        // TODO(BNCH-18256): Handle refresh/stale indicators if location is inaccessible.
        // Currently, we will not show stale indicators in this column, and refreshing will no-op for this column
        return 'NO_VALUE';
      }
      assert(location.__typename === 'LocationNode', 'Box can only be stored in a location.');

      return getTypedValueFromStorableSummaries([location]);
    }

    const matchingTag = box.tagsJson.find((tag: SchemaTag) =>
      isColumnForSchemaField(column, tag.tagSchemaField)
    );
    if (matchingTag) {
      return convertTagToTypedValue(matchingTag);
    }
    return 'NO_VALUE';
  },

  updateRow(
    model: BoxInfo | ParentStorableCapacityInfo | InaccessibleInfoGraphQL,
    columns: Array<StructuredTableColumn>,
    row: StructuredTableRow<BoxCreationTableRowMetadata>,
    columnGroup: ColumnGroup | null
  ): StructuredTableRow<BoxCreationTableRowMetadata> {
    if (isInaccessible(model)) {
      return row;
    }
    return {
      ...row,
      cells: buildStandardUpdatedCells(model, columns, row, columnGroup, this.updateCell),
    };
  },

  readFromCache(sampleLookupInfo: SampleLookupInfo): BoxInfo | ParentStorableCapacityInfo | null {
    const {idForLookup: apiIdentifier} = sampleLookupInfo;
    if (isUnfinalizedParentStorableSample(sampleLookupInfo)) {
      return readParentStorableCapacityFromCache(apiIdentifier);
    }
    assertAndContinue(
      APIUtil.isBox(apiIdentifier),
      'apiIdentifier must be for box when updating Box Creation Table'
    );
    if (!APIUtil.isBox(apiIdentifier)) {
      return null;
    }

    const data = readQuerySafe<BulkFetchBoxInfoForRowQuery, BulkFetchBoxInfoForRowQueryVariables>(
      {query: BULK_BOX_INFO_FOR_ROW_UPDATE_QUERY},
      {variables: {boxIds: [apiIdentifier]}, fetchPolicy: 'cache-only'}
    );
    if (data == null || !data.storables?.[0] || isInaccessible(data.storables[0])) {
      return null;
    }
    const box = data.storables[0];
    assert(box.__typename === 'BoxNode', 'Query must return box for box id');
    return box;
  },
};

export default BoxCreationTableRowUpdater;
